const HIGHLIGHT_NAME = 'page-highlight'
const cssHighlights: Map<string, unknown> | null = (typeof CSS !== 'undefined' && 'highlights' in CSS)
  ? (CSS as any).highlights as Map<string, unknown>
  : null

let highlightObserver: MutationObserver | null = null
let highlightDebounce: ReturnType<typeof setTimeout> | null = null
let highlightSafety: ReturnType<typeof setTimeout> | null = null
let retryTimers: ReturnType<typeof setTimeout>[] = []

function stopObserver(): void {
  if (highlightObserver) { highlightObserver.disconnect(); highlightObserver = null }
  if (highlightDebounce) { clearTimeout(highlightDebounce); highlightDebounce = null }
  if (highlightSafety) { clearTimeout(highlightSafety); highlightSafety = null }
  retryTimers.forEach(clearTimeout)
  retryTimers = []
}

export function clearPageHighlights(): void {
  stopObserver()
  cssHighlights?.delete(HIGHLIGHT_NAME)
}

export function highlightPageText(query: string): void {
  cssHighlights?.delete(HIGHLIGHT_NAME)
  const normalized = query.trim().toLowerCase()
  if (!normalized || !cssHighlights) return

  const walker = document.createTreeWalker(
    document.body,
    NodeFilter.SHOW_TEXT,
    {
      acceptNode(node: Node): number {
        const parent = (node as Text).parentElement
        if (!parent) return NodeFilter.FILTER_REJECT
        const tag = parent.tagName.toLowerCase()
        if (['script', 'style', 'mark', 'input', 'textarea', 'noscript'].includes(tag)) {
          return NodeFilter.FILTER_REJECT
        }
        return NodeFilter.FILTER_ACCEPT
      }
    }
  )

  const ranges: Range[] = []
  let node: Node | null
  while ((node = walker.nextNode())) {
    const text = (node as Text).textContent || ''
    const lower = text.toLowerCase()
    let start = 0
    let idx: number
    while ((idx = lower.indexOf(normalized, start)) !== -1) {
      const range = document.createRange()
      range.setStart(node, idx)
      range.setEnd(node, idx + normalized.length)
      ranges.push(range)
      start = idx + normalized.length
    }
  }

  if (ranges.length) {
    const highlight = new (window as any).Highlight(...ranges)
    cssHighlights.set(HIGHLIGHT_NAME, highlight)
  }
}

export function applyHighlightAndWatch(query: string): void {
  if (!query.trim()) return
  stopObserver()
  highlightPageText(query)

  const retryDelays = [300, 800, 2000, 5000, 10000, 15000, 20000]
  retryDelays.forEach(delay => {
    retryTimers.push(setTimeout(() => highlightPageText(query), delay))
  })

  highlightObserver = new MutationObserver(() => {
    if (highlightDebounce) clearTimeout(highlightDebounce)
    highlightDebounce = setTimeout(() => highlightPageText(query), 300)
  })
  highlightObserver.observe(document.body, { childList: true, subtree: true })
  highlightSafety = setTimeout(stopObserver, 30000)
}
